import { Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { pbjDailyHoursCapSortingKey, pbjLongShiftReviewSortingKey, pbjSingleDayFlagSortingKey } from "../../../services/LocalStorageService";
import type { PbjDailyHoursCapRow, PbjLongShiftRow, PbjModel, PbjModelUpsertBody } from "../../../utils/models";
import type { MultiSortOption } from "../../../utils/multiColumnSorting";
import { usePbjLongShiftReview, usePbjLongShifts, usePbjSubmissionHourCapReport } from "../hooks/usePbjAudit";
import { useUpsertPbjModel } from "../hooks/useUpsertPbjModel";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { boldNameColumn, hoursColumn, monospaceIdColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { CmsCapBadge } from "../SubmissionSheetsTab/CmsCapBadge";
import { ShiftDurationReviewSection } from "./ShiftDurationReviewSection";

type Props = {
	startDate: string;
	endDate: string;
	/** The dept's saved PBJ Model row — source for the editable Single Day Flag threshold + the upsert body. */
	savedModel: PbjModel;
};

/**
 * Read-only Long Shifts audit — two independent data-quality signals stacked in one tab:
 *
 * 1. **Long Shift Review** (primary): one row per individual shift whose gross clock-in/out duration exceeds a
 *    fixed per-shift threshold (sourced from the response, not hardcoded). Restores overnight shifts the
 *    per-day flag missed (they split at midnight). Renders through the shared ShiftDurationReviewSection —
 *    the same grid the Short Shifts tab uses for the opposite end of the distribution.
 * 2. **Single Day Flag** (existing): one row per (employee, dept-local day) whose summed net staffing hours cross
 *    the editable per-dept threshold. The threshold input lives on THIS section only.
 */
export const LongShiftsTable: FC<Props> = ({ startDate, endDate, savedModel }) => {
	const { t } = useTranslation();
	// Two independent endpoints (kept apart so neither response body changes shape): the per-shift
	// review (rows + its fixed threshold) and the per-day Single Day Flag (a bare array).
	const review = usePbjLongShiftReview(startDate, endDate);
	const singleDay = usePbjLongShifts(startDate, endDate);
	// (employee, day) rows trimmed to the CMS 22.5h/day cap in the filed submission — surfaced read-only
	// at the bottom of this tab so every override is auditable (mirrors the submission grid's tooltip).
	const cappedDays = usePbjSubmissionHourCapReport(startDate, endDate);
	const upsert = useUpsertPbjModel();

	// Editable threshold (hours) for the Single Day Flag audit only. Kept as a string for input ergonomics;
	// re-seeded whenever the saved row changes (dept switch / post-save refresh via updateUserInfo).
	const [thresholdInput, setThresholdInput] = useState(String(savedModel.longShiftThreshold));
	useEffect(() => setThresholdInput(String(savedModel.longShiftThreshold)), [savedModel.longShiftThreshold]);

	const parsedThreshold = Number(thresholdInput);
	const thresholdValid = Number.isFinite(parsedThreshold) && parsedThreshold > 0;
	const thresholdDirty = thresholdValid && parsedThreshold !== savedModel.longShiftThreshold;

	const saveThreshold = () => {
		if (!thresholdDirty) return;
		// Merge onto the saved row so no other PBJ Model field is clobbered; the upsert invalidates the
		// audit cache (so this list refilters) and refreshes the saved row via updateUserInfo.
		const body: PbjModelUpsertBody = { ...savedModel, longShiftThreshold: parsedThreshold };
		upsert.mutate(body);
	};

	const singleDayColumns: GridColDef<PbjLongShiftRow>[] = useMemo(
		() => [
			{ field: "empId", headerName: t("pbj_model.audit.long_shifts.cols.id"), minWidth: 140, flex: 1 },
			{ field: "name", headerName: t("pbj_model.audit.long_shifts.cols.name"), minWidth: 180, flex: 1 },
			{
				field: "date",
				headerName: t("pbj_model.audit.long_shifts.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value: string) => formatAuditDate(value),
			},
			hoursColumn<PbjLongShiftRow>("totalHours", t("pbj_model.audit.long_shifts.cols.total_hours"), {
				minWidth: 140,
				flex: 1,
				align: "left",
				headerAlign: "left",
			}),
			{
				field: "jobDescriptions",
				headerName: t("pbj_model.audit.long_shifts.cols.roles"),
				minWidth: 220,
				flex: 2,
				sortable: false,
				// DEFAULT: guard the join — the BE always sends jobDescriptions as an array (possibly []),
				// but a single cell getter must never throw and tear down the whole Audit tab via the error boundary.
				valueGetter: (_value, row) => (row.jobDescriptions ?? []).join(", "),
			},
		],
		[t],
	);

	// Roles is left out on purpose — the column is `sortable: false` (a joined array), so it has no axis.
	const singleDaySortingOptions = useMemo<MultiSortOption<PbjLongShiftRow>[]>(
		() => [
			{ label: t("pbj_model.audit.long_shifts.cols.id"), sortColumns: ["empId"] },
			{ label: t("pbj_model.audit.long_shifts.cols.name"), sortColumns: ["name"] },
			{ label: t("pbj_model.audit.long_shifts.cols.date"), sortColumns: ["date"] },
			{ label: t("pbj_model.audit.long_shifts.cols.total_hours"), sortColumns: ["totalHours"] },
		],
		[t],
	);

	// "Filed Hours" renders the capped value (the fixed cap, e.g. 22.5) with a tooltip citing the real
	// worked hours — the same CmsCapBadge the submission grid uses, so the two surfaces read identically.
	const cappedDayColumns: GridColDef<PbjDailyHoursCapRow>[] = useMemo(
		() => [
			monospaceIdColumn<PbjDailyHoursCapRow>("empId", t("pbj_model.audit.long_shifts.cols.id"), { minWidth: 140, flex: 1 }),
			boldNameColumn<PbjDailyHoursCapRow>("name", t("pbj_model.audit.long_shifts.cols.name"), { minWidth: 180, flex: 1 }),
			{
				field: "date",
				headerName: t("pbj_model.audit.long_shifts.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<PbjDailyHoursCapRow>("originalHours", t("pbj_model.audit.long_shifts.cols.original_hours"), {
				minWidth: 140,
				flex: 1,
				align: "left",
				headerAlign: "left",
			}),
			{
				field: "cappedHours",
				headerName: t("pbj_model.audit.long_shifts.cols.filed_hours"),
				minWidth: 160,
				flex: 1,
				type: "number",
				align: "left",
				headerAlign: "left",
				// Sort on the numeric value, but render the badge (capped value + tooltip citing the real hours).
				renderCell: (params) => (
					<CmsCapBadge cappedHours={params.row.cappedHours} originalHours={params.row.originalHours} dataTestId="pbj-daily-hours-cap-badge" />
				),
			},
		],
		[t],
	);

	const cappedDaySortingOptions = useMemo<MultiSortOption<PbjDailyHoursCapRow>[]>(
		() => [
			{ label: t("pbj_model.audit.long_shifts.cols.id"), sortColumns: ["empId"] },
			{ label: t("pbj_model.audit.long_shifts.cols.name"), sortColumns: ["name"] },
			{ label: t("pbj_model.audit.long_shifts.cols.date"), sortColumns: ["date"] },
			{ label: t("pbj_model.audit.long_shifts.cols.original_hours"), sortColumns: ["originalHours"] },
		],
		[t],
	);

	return (
		<Stack spacing={2.5} data-testid="pbj-long-shifts-tab">
			<ShiftDurationReviewSection
				title={t("pbj_model.audit.long_shifts.review.title")}
				// Threshold comes from the response (undefined while loading), so only show the number-bearing
				// subtitle once loaded — avoids an "exceeds  hours" flash and keeps the value single-sourced on the BE.
				description={review.data ? t("pbj_model.audit.long_shifts.review.subtitle", { hours: review.data.thresholdHours }) : undefined}
				emptyTitle={t("pbj_model.audit.long_shifts.review.empty", { hours: review.data?.thresholdHours })}
				errorMessage={t("pbj_model.audit.long_shifts.review.failed")}
				rows={review.data?.rows ?? []}
				loading={review.isLoading}
				error={review.isError}
				sectionTestId="pbj-long-shift-review-section"
				tableTestId="pbj-long-shift-review-table"
				emptyTestId="pbj-long-shift-review-empty"
				sortingLocalStorageKey={pbjLongShiftReviewSortingKey}
			/>

			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.long_shifts.single_day.title")}
				description={t("pbj_model.audit.long_shifts.single_day.subtitle")}
				dataTestId="pbj-single-day-flag-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<SystemDataGrid<PbjLongShiftRow>
					rows={singleDay.data ?? []}
					columns={singleDayColumns}
					getRowId={(r) => `${r.empId}-${r.date}`}
					sortingOptions={singleDaySortingOptions}
					localStorageKey={pbjSingleDayFlagSortingKey}
					searchableColumns={["empId", "name"]}
					loading={singleDay.isLoading}
					error={singleDay.isError}
					errorMessage={t("pbj_model.audit.long_shifts.failed")}
					emptyState={<EmptyState title={t("pbj_model.audit.long_shifts.empty")} dataTestId="pbj-single-day-flag-empty" />}
					defaultPageSize={25}
					containedHeight
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-single-day-flag-table"
					trailingToolbarNodes={
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", ml: "auto" }}>
							<Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
								{t("pbj_model.audit.long_shifts.threshold_label")}
							</Typography>
							<SystemValidationTextField
								inputType="number"
								value={thresholdInput}
								onChange={(e) => setThresholdInput(e.target.value)}
								reserveHeightForHelperText={false}
								formControlSx={{ width: 96 }}
								injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.5 } } }}
								dataTestId="pbj-long-shifts-threshold-input"
							/>
							<SystemButton
								buttonVariant={thresholdDirty ? "primary" : "secondary"}
								size="small"
								onClick={saveThreshold}
								disabled={!thresholdDirty}
								loading={upsert.isPending}
								noFullWidthMobile
								data-testid="pbj-long-shifts-threshold-save"
							>
								{t("pbj_model.audit.long_shifts.threshold_save")}
							</SystemButton>
						</Stack>
					}
				/>
			</SettingsSection>

			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.long_shifts.daily_cap.title")}
				// Cap comes from the response (undefined while loading) so the number-bearing subtitle only shows
				// once loaded — avoids a "capped to  hours" flash and keeps the value single-sourced on the BE.
				description={cappedDays.data ? t("pbj_model.audit.long_shifts.daily_cap.subtitle", { hours: cappedDays.data.capHours }) : undefined}
				dataTestId="pbj-daily-hours-cap-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<SystemDataGrid<PbjDailyHoursCapRow>
					rows={cappedDays.data?.rows ?? []}
					columns={cappedDayColumns}
					getRowId={(r) => `${r.empId}-${r.date}`}
					sortingOptions={cappedDaySortingOptions}
					localStorageKey={pbjDailyHoursCapSortingKey}
					searchableColumns={["empId", "name"]}
					loading={cappedDays.isLoading}
					error={cappedDays.isError}
					errorMessage={t("pbj_model.audit.submission_hour_cap_report.failed")}
					emptyState={
						<EmptyState title={t("pbj_model.audit.long_shifts.daily_cap.empty", { hours: cappedDays.data?.capHours })} dataTestId="pbj-daily-hours-cap-empty" />
					}
					defaultPageSize={25}
					containedHeight
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-daily-hours-cap-table"
				/>
			</SettingsSection>
		</Stack>
	);
};
